import posthog from 'posthog-js/dist/module.full.no-external'

import { logger } from '@/lib/observability/logger'
import { getIframeEnvironment } from '@/helpers/iframe'

import { isAnalyticsOptedOut } from './consent'
import { registerActiveExperiments } from './experiment-properties'
import { readGeoPropertiesFromMeta, registerGeoProperties } from './geo'
import { registerAnalyticsSessionId } from './session-id'

/**
 * Registers the page-load-scoped PostHog session properties: the iframe
 * embedding context (Safe, a partner, or top-level), coarse geo, and the
 * server-minted `session_id`. They are session properties, not person traits,
 * so `posthog.reset()` and a session id rotation both wipe them and they must
 * be re-registered after each. It also re-registers the persistent keys a
 * reset wipes: the session id and any active experiment variant.
 */
export function registerSessionProperties() {
  try {
    const { embedded, host } = getIframeEnvironment()
    posthog.register_for_session({ embedded, iframe_host: host ?? null })
  } catch {
    logger.warn('Failed to register iframe analytics properties')
  }

  registerGeoProperties(readGeoPropertiesFromMeta())
  registerAnalyticsSessionId()
  // Skipped while opted out: unlike the session properties above, this one
  // persists to localStorage, and `resetAnalyticsPerson` re-asserts the opt-out
  // just before calling us.
  if (!isAnalyticsOptedOut()) registerActiveExperiments()
}

/**
 * PostHog clears every `register_for_session` key when it rotates the session id
 * (idle timeout, max length, cross-tab adoption), so re-register ours on rotation.
 * Returns the unsubscribe function.
 */
export function registerSessionPropertiesOnRotation() {
  let currentSessionId: string | undefined
  return posthog.onSessionId(sessionId => {
    if (currentSessionId !== undefined && sessionId !== currentSessionId) {
      registerSessionProperties()
    }
    currentSessionId = sessionId
  })
}
